import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  BarChart3, 
  Settings,
  Eye,
  EyeOff,
  Target
} from "lucide-react";

/**
 * Financial Dashboard Preferences Component
 * 
 * Allows users to choose their preferred level of detail for the financial dashboard:
 * - Basic: Just the essentials (balance, income/outgoings, Centse score)
 * - Detailed: Full insights and recommendations
 */

export type DashboardDetailLevel = 'basic' | 'detailed';

interface FinanceDashboardPreferencesProps {
  currentLevel: DashboardDetailLevel;
  onLevelChange: (level: DashboardDetailLevel) => void;
  className?: string;
}

const FinanceDashboardPreferences = ({ 
  currentLevel, 
  onLevelChange, 
  className = "" 
}: FinanceDashboardPreferencesProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const preferenceLevels = [
    {
      id: 'basic' as DashboardDetailLevel,
      name: 'Basic View',
      description: 'Just the essentials - balance, income, outgoings, and Centse health',
      icon: Eye,
      features: [
        'Total balance & monthly income',
        'Essential spending overview', 
        'Centse Score',
        'Critical alerts only'
      ],
      color: 'text-green-600 bg-green-50 border-green-200'
    },
    {
      id: 'detailed' as DashboardDetailLevel,
      name: 'Detailed View',
      description: 'Complete financial insights with AI recommendations and transaction analysis',
      icon: BarChart3,
      features: [
        'Everything from Basic',
        'Transaction insights & patterns',
        'Full financial health breakdown',
        'AI-powered recommendations',
        'Detailed budget analysis'
      ],
      color: 'text-blue-600 bg-blue-50 border-blue-200'
    }
  ];

  return (
    <Card className={`bg-background border-gray-200 shadow-sm ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-secondary" />
            <CardTitle className="text-lg font-semibold text-gray-900">
              Dashboard Preferences
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs"
          >
            {isExpanded ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {isExpanded ? 'Hide Options' : 'Customize View'}
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          Choose how much detail you want to see in your financial dashboard
        </p>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <div className="space-y-4">
            {preferenceLevels.map((level) => {
              const LevelIcon = level.icon;
              const isActive = currentLevel === level.id;
              
              return (
                <div
                  key={level.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                    isActive 
                      ? `${level.color} border-2` 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => onLevelChange(level.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isActive ? 'bg-white/70' : 'bg-gray-100'
                    }`}>
                      <LevelIcon className={`h-4 w-4 ${
                        isActive ? level.color.split(' ')[0] : 'text-gray-600'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-gray-900">{level.name}</h3>
                        {isActive && (
                          <Badge variant="secondary" className="text-xs bg-white/70">
                            Active
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">
                        {level.description}
                      </p>
                      
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-700">Includes:</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {level.features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <Target className="h-3 w-3 text-gray-400" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Current setting: <strong>{preferenceLevels.find(l => l.id === currentLevel)?.name}</strong>
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsExpanded(false)}
                className="text-xs"
              >
                Done
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default FinanceDashboardPreferences;